import { SystemEntity, type TaskTypeView } from '@erp/shared-types';
import {
  getValueAtPath,
  readEntityReference,
  valueText,
} from './taskTypeViewValue';

export type SupplierDocumentLineView = {
  index: number;
  sku: string | null;
  description: string | null;
  upc: string | null;
  freeform: string[];
  matchLayer: string | null;
  productId: string | null;
  productVariantId: string | null;
  supplierProductId: string | null;
};

/**
 * A pick from the remap popover. `supplierProductId` is set when the person
 * chose one of the supplier's existing translations rather than a catalog
 * product.
 */
export type SupplierLineRemap = {
  productId?: string;
  productVariantId?: string;
  supplierProductId?: string;
};

const EMPTY_LINE_TEXT = '—';

export type SupplierLineRowDisplay = {
  primary: string;
  description: string;
  matchLayer: string;
  headerSku: string;
  headerDescription: string;
  selectedProductId: string | undefined;
  selectedVariantId: string | undefined;
  saveDisabled: boolean;
};

/** Strings and picker state for one remap row. The component only renders this. */
export function supplierLineRowDisplay(
  line: SupplierDocumentLineView,
  pending: SupplierLineRemap | null,
  missingDescription: string,
): SupplierLineRowDisplay {
  const selectedVariantId = chosenId(
    pending?.productVariantId,
    line.productVariantId,
  );
  return {
    primary: firstText(line.sku, line.upc),
    description: textOr(line.description, missingDescription),
    matchLayer: textOr(line.matchLayer, 'unmatched'),
    headerSku: firstText(line.sku, null),
    headerDescription: firstText(line.description, null),
    selectedProductId: chosenId(pending?.productId, line.productId),
    selectedVariantId,
    saveDisabled: selectedVariantId == null,
  };
}

function firstText(left: string | null, right: string | null): string {
  return left ?? right ?? EMPTY_LINE_TEXT;
}

function textOr(value: string | null, fallback: string): string {
  return value ?? fallback;
}

function chosenId(
  pending: string | undefined,
  current: string | null,
): string | undefined {
  if (pending != null) return pending;
  if (current != null) return current;
  return undefined;
}

/**
 * A catalog pick can teach the supplier, after a confirm, when the caller
 * may create supplier product matches and the line has a term to store.
 * An existing translation is already taught. No supplier, no permission,
 * or a blank line saves the task row only.
 */
export function supplierLineNeedsTranslationConfirm(args: {
  pick: SupplierLineRemap;
  supplierId: string | null;
  line: SupplierDocumentLineView;
  canCreateTranslation: boolean;
}): boolean {
  if (!args.canCreateTranslation || !args.supplierId) return false;
  if (args.pick.supplierProductId || !args.pick.productVariantId) return false;
  return supplierLineHasTranslationTerm(args.line);
}

/** The term named in the confirm. Sku, then upc, then description, then freeform. */
export function supplierLineTranslationTerm(
  line: SupplierDocumentLineView,
): string {
  return line.sku ?? line.upc ?? line.description ?? line.freeform[0] ?? '';
}

function supplierLineHasTranslationTerm(
  line: SupplierDocumentLineView,
): boolean {
  return supplierLineTranslationTerm(line) !== '';
}

/**
 * The supplier translation this line already has, when the new pick is a
 * different product. Same variant, or a pick of that same translation,
 * has nothing previous to remove.
 */
export function supplierLinePreviousMatch(args: {
  line: SupplierDocumentLineView;
  pick: SupplierLineRemap;
}): string | null {
  const previousId = args.line.supplierProductId;
  if (!previousId || !args.pick.productVariantId) return null;
  if (args.pick.productVariantId === args.line.productVariantId) return null;
  if (args.pick.supplierProductId === previousId) return null;
  return previousId;
}

/** A remap is saved only after a product variant is chosen. */
export function remapPick(
  pending: SupplierLineRemap | null,
): SupplierLineRemap | null {
  if (pending?.productVariantId == null) return null;
  return pending;
}

/** Read the fields the remap UI shows. Other keys on the row are left alone. */
export function readSupplierDocumentLine(
  value: unknown,
  fallbackIndex: number,
): SupplierDocumentLineView {
  const row = isRow(value) ? value : {};
  const identifiers = isRow(row.identifiers) ? row.identifiers : {};
  return {
    index: numberOr(row.index, fallbackIndex),
    sku: textOrNull(identifiers.sku),
    description: textOrNull(identifiers.description),
    upc: textOrNull(identifiers.upc),
    freeform: stringArray(identifiers.freeform),
    matchLayer: textOrNull(row.matchLayer),
    productId: textOrNull(row.productId),
    productVariantId: textOrNull(row.productVariantId),
    supplierProductId: textOrNull(row.supplierProductId),
  };
}

/**
 * Replace the product on one match row. The rest of the extracted row stays.
 * A pick from the supplier's translations records that translation; a
 * catalog pick records the variant alone. A cleared variant is unmatched.
 * This does not write a SupplierProduct.
 */
export function remapSupplierDocumentLine(
  value: unknown,
  pick: SupplierLineRemap,
): Record<string, unknown> {
  const row = isRow(value) ? { ...value } : {};
  const productVariantId = pick.productVariantId ?? null;
  const supplierProductId = productVariantId
    ? (pick.supplierProductId ?? null)
    : null;
  return {
    ...row,
    productId: pick.productId ?? null,
    productVariantId,
    supplierProductId,
    matchLayer: matchLayerForPick(productVariantId, supplierProductId),
    matchReason: 'manual',
  };
}

function matchLayerForPick(
  productVariantId: string | null,
  supplierProductId: string | null,
): 'supplier_product' | 'catalog' | 'unmatched' {
  if (!productVariantId) return 'unmatched';
  return supplierProductId ? 'supplier_product' : 'catalog';
}

/** Supplier id at a path: a bare id string or an `{ entityType, entityId }`. */
export function readSupplierId(
  root: Record<string, unknown>,
  supplierPath: readonly string[] | null,
): string | null {
  if (!supplierPath) return null;
  const value = getValueAtPath(root, supplierPath);
  if (typeof value === 'string') return textOrNull(value);
  const reference = readEntityReference(value);
  if (!reference || reference.entityType !== SystemEntity.AP_SUPPLIER) {
    return null;
  }
  return reference.entityId;
}

export type SupplierLineRematchPlan = {
  path: string[];
  supplierId: string;
};

/**
 * Placements whose supplier changes between the current working copy and
 * the next one. Those rows were matched for a different supplier, so the
 * commit matches them again before it is saved. Clearing the supplier does
 * not rematch; the rows keep what they had.
 */
export function supplierLineRematchPlans(
  view: TaskTypeView,
  workingCopy: Record<string, unknown>,
  next: Record<string, unknown>,
): SupplierLineRematchPlan[] {
  const plans: SupplierLineRematchPlan[] = [];
  for (const region of view.regions) {
    for (const placement of region.placements) {
      if (placement.component !== 'supplierLineMatch') continue;
      const before = readSupplierId(workingCopy, placement.config.supplierPath);
      const after = readSupplierId(next, placement.config.supplierPath);
      if (!after || after === before) continue;
      plans.push({ path: [...placement.config.path], supplierId: after });
    }
  }
  return plans;
}

/** Text shown in an extracted-column control. Objects are not editable here. */
export function formatSupplierLineColumn(value: unknown): string {
  if (typeof value === 'string') return value;
  return valueText(value) ?? '';
}

/**
 * Value written back from an extracted-column control. Empty clears the
 * key. When the stored value is a number and the edit still parses as a
 * finite number, a number is written so a quantity stays a quantity.
 */
export function parseSupplierLineColumn(
  draft: string,
  current: unknown,
): unknown {
  const trimmed = draft.trim();
  if (trimmed === '') return null;
  if (typeof current !== 'number') return draft;
  const asNumber = Number(trimmed);
  return Number.isFinite(asNumber) ? asNumber : draft;
}

function isRow(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function textOrNull(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

function stringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    const text = textOrNull(item);
    return text ? [text] : [];
  });
}

function numberOr(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isInteger(value)
    ? value
    : fallback;
}
